import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 320;
const MARGIN = { top: 30, right: 30, bottom: 50, left: 60 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function ForecastChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.historical || data.historical.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const historical = data.historical;
    const forecast = data.forecast || [];
    const trend = data.trend || {};

    // Build combined date domain
    const allDates = [
      ...historical.map((d) => new Date(d.date)),
      ...forecast.map((d) => new Date(d.date)),
    ];

    // Build combined price domain
    const allPrices = [
      ...historical.map((d) => d.minPrice),
      ...historical.map((d) => d.maxPrice),
      ...forecast.map((d) => d.lower),
      ...forecast.map((d) => d.upper),
    ];

    const xExtent = d3.extent(allDates);
    const priceMin = d3.min(allPrices);
    const priceMax = d3.max(allPrices);
    const pricePadding = (priceMax - priceMin) * 0.1 || 1;

    // Scales
    const x = d3.scaleTime()
      .domain(xExtent)
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([priceMin - pricePadding, priceMax + pricePadding])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(allDates.length, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickFormat((v) => `$${v.toFixed(2)}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Boundary between historical and forecast
    const boundaryDate = new Date(historical[historical.length - 1].date);
    const boundaryX = x(boundaryDate);

    if (forecast.length > 0) {
      g.append('line')
        .attr('x1', boundaryX)
        .attr('x2', boundaryX)
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '4,3');
    }

    // ── Historical section ──

    // Shaded band (min to max)
    const historicalArea = d3.area()
      .x((d) => x(new Date(d.date)))
      .y0((d) => y(d.minPrice))
      .y1((d) => y(d.maxPrice))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(historical)
      .attr('d', historicalArea)
      .attr('fill', '#2563eb')
      .attr('fill-opacity', 0.1)
      .attr('stroke', 'none');

    // Average price line
    const historicalLine = d3.line()
      .x((d) => x(new Date(d.date)))
      .y((d) => y(d.avgPrice))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(historical)
      .attr('d', historicalLine)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2);

    // Dots on historical avg
    g.selectAll('.hist-dot')
      .data(historical)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(new Date(d.date)))
      .attr('cy', (d) => y(d.avgPrice))
      .attr('r', 3)
      .attr('fill', '#2563eb');

    // Invisible hover targets for historical points
    g.selectAll('.hist-hover')
      .data(historical)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(new Date(d.date)))
      .attr('cy', (d) => y(d.avgPrice))
      .attr('r', 10)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')
      .on('mousemove', (event, d) => {
        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          type: 'historical',
          date: d.date,
          avgPrice: d.avgPrice,
          minPrice: d.minPrice,
          maxPrice: d.maxPrice,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    // ── Forecast section ──
    if (forecast.length > 0) {
      // Connect historical to forecast with a bridge point
      const bridgeData = [
        historical[historical.length - 1],
        ...forecast.map((d) => ({
          date: d.date,
          avgPrice: d.predicted,
          minPrice: d.lower,
          maxPrice: d.upper,
        })),
      ];

      // Confidence band
      const forecastArea = d3.area()
        .x((d) => x(new Date(d.date)))
        .y0((d) => y(d.minPrice))
        .y1((d) => y(d.maxPrice))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(bridgeData)
        .attr('d', forecastArea)
        .attr('fill', '#8b5cf6')
        .attr('fill-opacity', 0.12)
        .attr('stroke', 'none');

      // Predicted line (dashed)
      const forecastLine = d3.line()
        .x((d) => x(new Date(d.date)))
        .y((d) => y(d.avgPrice))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(bridgeData)
        .attr('d', forecastLine)
        .attr('fill', 'none')
        .attr('stroke', '#8b5cf6')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,3');

      // Invisible hover targets for forecast points
      g.selectAll('.forecast-hover')
        .data(forecast)
        .enter()
        .append('circle')
        .attr('cx', (d) => x(new Date(d.date)))
        .attr('cy', (d) => y(d.predicted))
        .attr('r', 10)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event, d) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'forecast',
            date: d.date,
            predicted: d.predicted,
            lower: d.lower,
            upper: d.upper,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    }

    // ── Trend summary box (top-right of chart area) ──
    if (trend.monthlyChange != null && trend.rSquared != null) {
      const boxWidth = 140;
      const boxHeight = 46;
      const boxX = innerW - boxWidth - 8;
      const boxY = 8;

      const trendG = g.append('g').attr('transform', `translate(${boxX}, ${boxY})`);

      trendG.append('rect')
        .attr('width', boxWidth)
        .attr('height', boxHeight)
        .attr('rx', 6)
        .attr('fill', 'rgba(255, 255, 255, 0.9)')
        .attr('stroke', '#e5e7eb')
        .attr('stroke-width', 1);

      const directionUp = trend.direction === 'up';
      const directionDown = trend.direction === 'down';
      const trendColor = directionUp ? '#16a34a' : directionDown ? '#dc2626' : '#6b7280';
      const arrow = directionUp ? '\u2191' : directionDown ? '\u2193' : '\u2014';
      const sign = trend.monthlyChange >= 0 ? '+' : '';

      trendG.append('text')
        .attr('x', 8)
        .attr('y', 18)
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', trendColor)
        .text(`${arrow} Trend: ${sign}$${Math.abs(trend.monthlyChange).toFixed(2)}/mo`);

      trendG.append('text')
        .attr('x', 8)
        .attr('y', 34)
        .attr('font-size', '10px')
        .attr('fill', '#9ca3af')
        .text(`R\u00B2 = ${trend.rSquared.toFixed(2)}`);
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Handle window resize
  useEffect(() => {
    if (!data || !data.historical || data.historical.length === 0) return;

    const handleResize = () => {
      // Trigger re-render by updating a dependency-free ref check
      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();
      // Re-dispatch the main effect by forcing a state update
      setTooltip(null);
    };

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [data]);

  // Empty state
  if (!data || !data.historical || data.historical.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Trend & Forecast</div>
          <div style={styles.subtitle}>Linear regression with 90-day projection</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          Not enough data for forecast
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Trend & Forecast</div>
        <div style={styles.subtitle}>Linear regression with 90-day projection</div>
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2" />
          </svg>
          <span>Historical Average</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#2563eb" fillOpacity="0.1" rx="2" />
          </svg>
          <span>Min/Max Range</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="4,2" />
          </svg>
          <span>Forecast</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#8b5cf6" fillOpacity="0.12" rx="2" />
          </svg>
          <span>Confidence Band</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltip.type === 'historical' ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Avg</span>
                <span style={{ fontWeight: 600, color: '#2563eb' }}>
                  ${tooltip.avgPrice.toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Range</span>
                <span style={{ fontWeight: 600 }}>
                  ${tooltip.minPrice.toFixed(2)}&ndash;${tooltip.maxPrice.toFixed(2)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Predicted</span>
                <span style={{ fontWeight: 600, color: '#8b5cf6' }}>
                  ${tooltip.predicted.toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Range</span>
                <span style={{ fontWeight: 600 }}>
                  ${tooltip.lower.toFixed(2)}&ndash;${tooltip.upper.toFixed(2)}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
